(ns frontend.view.reports.custom.capman-buyout.view
  (:require
   [frontend.ui.component.chart.view :refer [chart]]
   [frontend.db :as app-db]
   [potpuri.core :refer [deep-merge]]
   [reagent.core :as r]
   [common.time :as c-time]
   [common.dates :as dates]
   [frontend.state.current-session :as session]
   [common.util :as cu]
   [frontend.utils :as fu]
   [common.entity :as entity]
   [frontend.bootstrap.components :as rbs]
   [ui.components.card.view :as card]))

(def capman-colors
  ["#1bc9b9"
   "#c38573"
   "#e1ddd5"
   "#b5bbc2"
   "#1c9489"
   "#845244"
   "#c3beb7"
   "#83898f"
   "#7ae6d8"
   "#f0baab"
   "#f1eeea"
   "#54362e"
   "#8e8a83"
   "#363636"])

(defn ^{:jsdoc ["@nocollapse"]} format-data-point
  [_]
  (this-as js-data-point
           (let [data-point (js->clj js-data-point :keywordize-keys true)
                 {:keys [y x]} data-point]
             (if (and (number? y) (not= 0 y))
               y
               ""))))

(defn default-width
  []
  (let [window-width (:width @app-db/window)
        paddings-and-margins-when-single-item 210]
    (if (< window-width 768)
      (- window-width paddings-and-margins-when-single-item)
      (min (- window-width paddings-and-margins-when-single-item)
           1150))))


(defn date->quarter
  [dt]
  (let [month (inc (.getMonth dt))
        Q (-> month
              (/ 3)
              (double)
              (Math/ceil)
              (int))]
    (str Q "Q" (.getYear dt))))

(defn oppos-created-between
  [oppos start-ms end-ms]
  (->> oppos
       (filter (fn [o]
                 (<= start-ms (:common/created-on o) end-ms)))))

(defn oppos-put-on-hold-between
  [oppos start-ms end-ms]
  (->> oppos
       (filter (fn [o]
                 (<= start-ms (:oppo/put-on-hold-on o) end-ms)))))

(defn oppos-passed-between
  [oppos start-ms end-ms]
  (->> oppos
       (filter (fn [o]
                 (<= start-ms (:oppo/passed-on o) end-ms)))))

(defn oppos-reached-stage
  [oppos stage-id start-ms end-ms]
  (->> oppos
       (filter (fn [oppo]
                 (->> oppo
                      :common/change-meta
                      (some (fn [change]
                              (and (= :change.type/stage-change (:change/type change))
                                   (= stage-id
                                      (some->> change
                                               :change/value
                                               :enter-stage))
                                   (<= start-ms (:common/created-on change) end-ms)))))))))

(defn selected-quarter-summary
  [selected-months-diff selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id))
        a3-stage-id (->> (session/pipeline-stages selected-pipeline-id)
                         (cu/filter-by :stage/name "A3")
                         (first)
                         :db/id)
        now (dates/date)
        selected-q (dates/minus now (dates/months selected-months-diff))
        start-ms (c-time/start-of-q-millis selected-q)
        end-ms (c-time/end-of-q-millis selected-q)
        created-oppos (oppos-created-between oppos start-ms end-ms)
        put-on-hold-oppos (oppos-put-on-hold-between oppos start-ms end-ms)
        passed-oppos (oppos-passed-between oppos start-ms end-ms)
        reached-stage (oppos-reached-stage oppos a3-stage-id start-ms end-ms)]
    [:div
     [chart
      {:series      [{:data [(count created-oppos)
                             (count put-on-hold-oppos)
                             (count passed-oppos)
                             (count reached-stage)]}]
       :chart       {:type         "column"
                     :backgroundColor "#FFFFFF"
                     :borderRadius 10
                     :width        (default-width)}
       :colors      capman-colors
       :xAxis       [{:categories ["New cases"
                                   "Put on hold"
                                   "Passed"
                                   "Entered A3"]
                      :crosshair  true}]
       :yAxis       {:labels {:format "{value}"}
                     :title  nil}
       :title       {:text "New entries selected quarter"}
       :credits     {:enabled false}
       :legend      {:enabled false}
       :borderWidth 1
       :shadow      false
       :tooltip     {:headerFormat "<b>{point.name}</b><br/>"
                     :pointFormat  "{point.name}: {point.y}"}
       :plotOptions {:series {:dataLabels {:enabled true
                                           :format  "{point.y}"}}
                     :column {:stacking   "normal"
                              :dataLabels {:enabled       true
                                           :style         {:textOutline 0}
                                           :borderWidth   0
                                           :verticalAlign "top"
                                           :formatter     format-data-point}}}}]]))

(defn pass-reasons
  [selected-months-diff selected-pipeline-id]
  (let [passed-oppos (->> @session/oppos
                          (cu/filter-by :oppo/pipeline selected-pipeline-id)
                          (cu/filter-by :oppo/status :oppo.status/passed))
        all-pass-count (count passed-oppos)
        now (dates/date)
        selected-q (dates/minus now (dates/months selected-months-diff))
        q-start-ms (c-time/start-of-q-millis selected-q)
        q-end-ms (c-time/end-of-q-millis selected-q)
        passed-this-q (oppos-passed-between passed-oppos q-start-ms q-end-ms)
        this-q-pass-count (count passed-this-q)
        passed-this-q-per-reason (->> passed-this-q
                                      (group-by :oppo/pass-reason-v2)
                                      (map (fn [[pr oppos]]
                                             [pr (count oppos)]))
                                      (into {}))]
    [:table.zapflow-table.table.table-striped.table-bordered.table-hover
     {:style {:width "100%"}}
     [:thead
      [:tr
       [:th "Top pass reasons"]
       [:th "Selected quarter"]
       [:th "All time"]]]
     [:tbody
      (->> passed-oppos
           (group-by :oppo/pass-reason-v2)
           (map (fn [[pr oppos]]
                  (let [pass-reason (session/get-entity pr)
                        this-q (get passed-this-q-per-reason pr)]
                    [(or (:named-value/value pass-reason) "Unknown")
                     (when (and (not (zero? this-q-pass-count))
                                (number? this-q))
                       (/ this-q this-q-pass-count))
                     (when-not (zero? all-pass-count)
                       (/ (count oppos) all-pass-count))])))
           (sort-by (fn [[_ q _]]
                      (or q -1)))
           (reverse)
           (map-indexed (fn [idx [l q a]]
                          ^{:key (str "report-pass-reasons-row-" l "-" idx)}
                          [:tr
                           [:td l]
                           [:td
                            (if (zero? q)
                              "-"
                              (fu/format-percentage q))]
                           [:td
                            (if (zero? a)
                              "-"
                              (fu/format-percentage a))]]))
           (doall))]]))

(defn source-categories
  [selected-months-diff selected-pipeline-id]
  (let [all-oppos (->> @session/oppos
                       (cu/filter-by :oppo/pipeline selected-pipeline-id))
        all-count (count all-oppos)
        now (dates/date)
        selected-q (dates/minus now (dates/months selected-months-diff))
        q-start-ms (c-time/start-of-q-millis selected-q)
        q-end-ms (c-time/end-of-q-millis selected-q)
        oppos-created-this-q (oppos-created-between all-oppos q-start-ms q-end-ms)
        this-q-create-count (count oppos-created-this-q)
        this-q-per-source (->> oppos-created-this-q
                               (group-by :oppo/lead-source-v2)
                               (map (fn [[pr oppos]]
                                      [pr (count oppos)]))
                               (into {}))]
    [:table.zapflow-table.table.table-striped.table-bordered.table-hover
     {:style {:width "100%"}}
     [:thead
      [:tr
       [:th "Source categories"]
       [:th "Selected quarter"]
       [:th "All time"]]]
     [:tbody
      (->> all-oppos
           (group-by :oppo/lead-source-v2)
           (map (fn [[ls oppos]]
                  (let [lead-source (session/get-entity ls)
                        this-q (get this-q-per-source ls)]
                    [(or (:named-value/value lead-source) "Unknown")
                     (when (and (not (zero? this-q-create-count))
                                (number? this-q))
                       (float (/ this-q this-q-create-count)))
                     (when-not (zero? all-count)
                       (float (/ (count oppos) all-count)))])))
           (sort-by (fn [[_ q _]]
                      (or q -1)))
           (reverse)
           (map (fn [[l q a]]
                  ^{:key (str "report-pass-reasons-row-" l)}
                  [:tr
                   [:td l]
                   [:td
                    (if (zero? q)
                      "-"
                      (fu/format-percentage q))]
                   [:td
                    (if (zero? a)
                      "-"
                      (fu/format-percentage a))]]))
           (doall))]]))

(def month-names ["January" "February" "March" "April" "May" "June" "July" "August" "September" "October" "November" "December"])

(defn cases-ltm
  [selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id))
        now (dates/date)
        last-year (dates/minus now (dates/months 12))
        start-ms (c-time/start-of-month-millis last-year)
        end-ms (c-time/now-millis)
        created-oppos (oppos-created-between oppos start-ms end-ms)
        month (.getMonth (js/Date.))
        ltm-month-names (concat (subvec month-names month (count month-names))
                                (subvec month-names 0 month))
        created-by-month (->> created-oppos
                              (group-by (fn [oppo]
                                          (->> oppo
                                               :common/created-on
                                               (js/Date.)
                                               (.getMonth))))
                              (map (fn [[month oppos]]
                                     [month (count oppos)]))
                              (into {}))
        created-by-counts (->> (concat (range month 12)
                                       (range 0 month))
                               (mapv (fn [m]
                                       (or (get created-by-month m)
                                           0))))]
    [:div
     [chart
      {:series      [{:data created-by-counts}]
       :chart       {:type         "line"
                     :backgroundColor "#FFFFFF"
                     :borderRadius 10
                     :width        (default-width)}
       :colors      capman-colors
       :xAxis       [{:categories ltm-month-names
                      :crosshair  true}]
       :yAxis       {:labels {:format "{value}"}
                     :title  {:text "Count"}}
       :title       {:text "New cases per month LTM"}
       :credits     {:enabled false}
       :legend      {:enabled false}
       :borderWidth 1
       :shadow      false
       :tooltip     {:headerFormat "<b>{point.name}</b><br/>"
                     :pointFormat  "{point.name}: {point.y}"}
       :plotOptions {:series {:dataLabels {:enabled true
                                           :format  "{point.y}"}}
                     :column {:stacking   "normal"
                              :dataLabels {:enabled       true
                                           :style         {:textOutline 0}
                                           :borderWidth   0
                                           :verticalAlign "top"
                                           :formatter     format-data-point}}}}]]))

(defn sources-ltm
  [selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id))
        now (dates/date)
        last-year (dates/minus now (dates/months 12))
        start-ms (c-time/start-of-month-millis last-year)
        end-ms (c-time/now-millis)
        created-oppos (oppos-created-between oppos start-ms end-ms)
        month (.getMonth (js/Date.))
        ltm-month-names (concat (subvec month-names month (count month-names))
                                (subvec month-names 0 month))
        lead-source-ids (->> created-oppos
                             (map :oppo/lead-source-v2)
                             (distinct)
                             (sort-by (fn [id]
                                        (->> id
                                             (session/get-entity)
                                             :named-value/value
                                             (cu/str-trim-lcase)))))
        oppos-per-month (->> created-oppos
                             (group-by (fn [oppo]
                                         (->> oppo
                                              :common/created-on
                                              (js/Date.)
                                              (.getMonth))))
                             (into {}))
        lead-sources-by-month-datas (->> lead-source-ids
                                         (map session/get-entity)
                                         (mapv (fn [ls]
                                                 {:name (or (:named-value/value ls) "Unknown")
                                                  :data (->> (concat (range month 12)
                                                                     (range 0 month))
                                                             (mapv (fn [month]
                                                                     (->> month
                                                                          (get oppos-per-month)
                                                                          (cu/filter-by :oppo/lead-source-v2 (:db/id ls))
                                                                          (count)))))})))]
    [:div
     [chart
      {:series      lead-sources-by-month-datas
       :chart       {:type         "column"
                     :backgroundColor "#FFFFFF"
                     :borderRadius 10
                     :width        (default-width)}
       :colors      capman-colors
       :xAxis       [{:categories        ltm-month-names
                      :tickmarkPlacement "on"}]
       :yAxis       {:labels {:format "{value}"}
                     :title  {:text "Count"}}
       :title       {:text "Sources LTM"}
       :borderWidth 1
       :shadow      false
       :plotOptions {:series {:dataLabels {:enabled true
                                           :format  "{point.y}"}}
                     :column {:stacking "normal"}}}]]))

(defn- oppo-counts-by-geography
  [oppos]
  (->> oppos
       (mapcat (fn [oppo]
                 (let [countries (some-> oppo
                                         :oppo/target-asset
                                         (session/get-entity)
                                         :asset/country)]
                   (concat []
                           (when (some #{:FI} countries)
                             [["Finland" oppo]])
                           (when (some #{:SE} countries)
                             [["Sweden" oppo]])
                           (when (->> countries
                                      (remove #{:SE :FI})
                                      (seq))
                             [["Other" oppo]])
                           (when (empty? countries)
                             [["Unknown" oppo]])))))
       (group-by first)
       (mapv (fn [[n oppos]]
               {:name n
                :y    (count oppos)}))))

(defn- oppo-counts-by-deal-sector-names
  [oppos]
  (->> oppos
       (mapcat (fn [o]
                 (let [sectors (:oppo/tag-sector o)]
                   (concat []
                           (when (empty? sectors)
                             [["Unknown" o]])
                           (->> sectors
                                (keep (fn [t]
                                        (when-let [l (some->>
                                                      t
                                                      (session/get-entity)
                                                      :named-value/value)]
                                          [l o])))
                                (doall))))))
       (group-by first)
       (mapv (fn [[n oppos]]
               {:name n
                :y    (count oppos)}))))

(defn- oppo-counts-by-company-tag-names
  [oppos]
  (->> oppos
       (mapcat (fn [o]
                 (let [asset (session/get-entity (:oppo/target-asset o))
                       tags (:company/tag-tags asset)]
                   (when asset
                     (concat []
                             (when (empty? tags)
                               [["Unknown" o]])
                             (->> tags
                                  (keep (fn [t]
                                          (when-let [l (some->>
                                                        t
                                                        (session/get-entity)
                                                        :named-value/value)]
                                            [l o])))
                                  (doall)))))))
       (group-by first)
       (mapv (fn [[tag-name oppos]]
               {:name tag-name
                :y    (count oppos)}))))

(defn new-cases
  [selected-months-diff selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id))
        now (dates/date)
        selected-q (dates/minus now (dates/months selected-months-diff))
        start-ms (c-time/start-of-q-millis selected-q)
        end-ms (c-time/end-of-q-millis selected-q)
        created-oppos (oppos-created-between oppos start-ms end-ms)
        oppos-by-geography (oppo-counts-by-geography created-oppos)
        oppos-by-deal-sector (oppo-counts-by-deal-sector-names created-oppos)
        oppos-by-company-tag (oppo-counts-by-company-tag-names created-oppos)]
    [:<>
     [:div
      {:class-name "col-span-4"}
      [card/card
       [chart
        {:series [{:data oppos-by-geography}]
         :title  {:text "Deals by country tag"}
         :chart  {:type "pie"}
         :colors capman-colors}]]]
     [:div
      {:class-name "col-span-4"}
      [card/card
       [chart
        {:series [{:data oppos-by-deal-sector}]
         :title  {:text "Deals by sector tag"}
         :chart  {:type "pie"}
         :colors capman-colors}]]]
     [:div
      {:class-name "col-span-4"}
      [card/card
       [chart
        {:series [{:data oppos-by-company-tag}]
         :title  {:text "Deals by company tag"}
         :chart  {:type "pie"}
         :colors capman-colors}]]]]))

(defn current-deals-per-stage
  [selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id)
                   (cu/filter-by :oppo/status :oppo.status/ok))
        pipeline-stages (session/pipeline-stages selected-pipeline-id)
        stage-names ["Monitor" "Approach" "Opportunity" "Prioritized" "A3"]
        stage-ids (->> stage-names
                       (mapv (fn [sn]
                               (->> pipeline-stages
                                    (filter (fn [s]
                                              (= (:stage/name s) sn)))
                                    (first)
                                    :db/id))))
        data (->> stage-ids
                  (mapv (fn [sid]
                          (->> oppos
                               (cu/filter-by :oppo/stage sid)
                               (count)))))]
    [:div
     [chart
      {:chart       {:type "column"}
       :colors      capman-colors
       :series      [{:data data}]
       :xAxis       [{:categories stage-names
                      :crosshair  true}]
       :yAxis       {:labels {:format "{value}"}
                     :title  {:text "Count"}}
       :title       nil
       :credits     {:enabled false}
       :legend      {:enabled false}
       :borderWidth 1
       :shadow      false
       :tooltip     {:headerFormat "<b>{point.name}</b><br/>"
                     :pointFormat  "{point.name}: {point.y}"}
       :plotOptions {:series {:dataLabels {:enabled true
                                           :format  "{point.y}"}}
                     :column {:stacking   "normal"
                              :dataLabels {:enabled       true
                                           :style         {:textOutline 0}
                                           :borderWidth   0
                                           :verticalAlign "top"
                                           :formatter     format-data-point}}}}]]))

(defn current-deals-per-stages-and-owner
  [selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id)
                   (cu/filter-by :oppo/status :oppo.status/ok))
        pipeline-stages (session/pipeline-stages selected-pipeline-id)
        stage-names ["Monitor" "Approach" "Opportunity" "Prioritized" "A3"]
        stage-ids (->> stage-names
                       (keep (fn [sn]
                               (->> pipeline-stages
                                    (filter (fn [s]
                                              (= (:stage/name s) sn)))
                                    (first)
                                    :db/id)))
                       (vec))]
    [:div.table-responsive
     [:table.zapflow-table.table.table-striped.table-bordered.table-hover
      [:thead
       [:tr
        [:th "Owner"]
        (->> stage-names
             (map (fn [sn]
                    ^{:key (str "buyout-report-deals-per-stage-and-owner-head-" sn)}
                    [:th sn]))
             (doall))]]
      [:tbody
       (->> oppos
            (group-by :oppo/owner)
            (sort-by (fn [[owner-id oppos]]
                       (->> owner-id
                            (session/get-entity)
                            (:account/first-name)
                            (cu/str-trim-lcase))))
            (map (fn [[owner-id oppos]]
                   ^{:key (str "buyout-report-deals-per-stage-and-owner-" owner-id)}
                   [:tr
                    [:td
                     (or (:account/first-name (session/get-entity owner-id))
                         "Unknown")]
                    (->> stage-ids
                         (map (fn [stage-id]
                                ^{:key (str "buyout-report-deals-per-stage-and-owner-" owner-id "-" stage-id)}
                                [:td
                                 (or (some->> oppos
                                              (cu/filter-by :oppo/stage stage-id)
                                              (count))
                                     "-")]))
                         (doall))]))
            (doall))]]]))

(defn current-deals-distributions
  [selected-pipeline-id]
  (let [oppos (->> @session/oppos
                   (cu/filter-by :oppo/pipeline selected-pipeline-id)
                   (cu/filter-by :oppo/status :oppo.status/ok))
        oppos-by-geography (oppo-counts-by-geography oppos)
        oppos-by-deal-sector (oppo-counts-by-deal-sector-names oppos)
        oppos-by-company-tag (oppo-counts-by-company-tag-names oppos)
        oppos-by-source (->> oppos
                             (group-by :oppo/lead-source-v2)
                             (mapv (fn [[tag oppos]]
                                     {:name (if-let [n (->> tag
                                                            (session/get-entity)
                                                            :named-value/value)]
                                              n
                                              "Unknown")
                                      :y    (count oppos)})))]
    [:<>
     [:div
      {:class-name "grid grid-cols-2 gap-4 pt-2"}
      [:div
       [card/card
        [chart
         {:series [{:data oppos-by-geography}]
          :title  {:text "By geography"}
          :chart  {:type "pie"}
          :colors capman-colors}]]]
      [:div
       [card/card
        [chart
         {:series [{:data oppos-by-deal-sector}]
          :title  {:text "By deal tag"}
          :chart  {:type "pie"}
          :colors capman-colors}]]]]
     [:div
      {:class-name "grid grid-cols-2 gap-4 pt-2"}
      [:div
       [card/card
        [chart
         {:series [{:data oppos-by-company-tag}]
          :title  {:text "By company tag"}
          :chart  {:type "pie"}
          :colors capman-colors}]]]
      [:div
       [card/card
        [chart
         {:series [{:data oppos-by-source}]
          :title  {:text "By source"}
          :chart  {:type "pie"}
          :colors capman-colors}]]]]]))

(defn view
  [args]
  (let [view-atom (r/atom {:quarter 0})]
    (fn [args]
      (let [buyout-pipeline-id (->> @session/pipelines
                                    (cu/filter-by :pipeline/entity-type :entity.type/oppo)
                                    (cu/filter-by :pipeline/name "Buyout")
                                    (first)
                                    :db/id)
            now (c-time/now-millis)
            first-oppo-created (->> @session/oppos
                                    (cu/filter-by :oppo/pipeline buyout-pipeline-id)
                                    (keep :common/created-on)
                                    (apply min))
            selected-months-diff (:quarter @view-atom)]
        [:div
         {:style {:max-width "1200px"}}
         [:h2
          {:style {:font-size  "32px"
                   :text-align "Center"}}
          "Deal flow periodic report"]
         [:h3
          {:style {:font-size  "20px"
                   :text-align "Center"}}
          "Period: "
          [:select
           {:value     selected-months-diff
            :on-change (fn [e]
                         (swap! view-atom assoc
                           :quarter
                           (->> e
                                .-target
                                .-value)))}
           [:option
            {:value 0
             :label "This quarter"}]
           [:option
            {:value 3
             :label "Last quarter"}]
           [:option
            {:value 6
             :label "-2Q"}]
           [:option
            {:value 9
             :label "-3Q"}]
           [:option
            {:value 12
             :label "-4Q"}]]]
         [:div
          {:class-name "grid grid-cols-1 gap-4"}
          [:div
           [card/card
            [selected-quarter-summary selected-months-diff buyout-pipeline-id]]]]
         [:div
          {:class-name "grid grid-cols-2 gap-4 pt-2"}
          [:div
           [card/card
            [pass-reasons selected-months-diff buyout-pipeline-id]]]
          [:div
           [card/card
            [source-categories selected-months-diff buyout-pipeline-id]]]]
         [:div
          {:class-name "grid grid-cols-1 gap-4 pt-2"}
          [:div
           [card/card
            [cases-ltm buyout-pipeline-id]]]]
         [:div
          {:class-name "grid grid-cols-1 gap-4 pt-2"}
          [:div
           [card/card
            [sources-ltm buyout-pipeline-id]]]]
         [:div
          {:class-name "grid grid-cols-1 gap-4 pt-2"}
          [new-cases selected-months-diff buyout-pipeline-id]]

         [:h2
          {:style {:font-size  "32px"
                   :text-align "Center"}}
          "Deal flow Snapshot report"]
         [:h3
          {:style {:font-size  "20px"
                   :text-align "Center"}}
          "Period: current status"]
         [:div
          {:class-name "grid grid-cols-1 gap-4 pt-2"}
          [:div
           [card/card
            [current-deals-per-stage buyout-pipeline-id]]]]
         [:div
          {:class-name "grid grid-cols-1 gap-4 pt-2"}
          [:div
           [card/card
            [current-deals-per-stages-and-owner buyout-pipeline-id]]]]
         [current-deals-distributions buyout-pipeline-id]]))))
